import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { Banknote, BarChart3, CirclePlus, MessageSquare, Store } from 'lucide-react';
import { get } from '@/lib/api';
import { useSession } from '@/lib/auth';
import { greeting, todayLine } from '@/lib/time';
import { KpiTile } from '@/components/domain/KpiTile';
import { ActivityFeedItem, type ActivityEvent } from '@/components/domain/ActivityFeedItem';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { dollars } from '@/lib/payouts';
import { storePath } from '@/lib/store';

type KpiPayload = Record<'pending' | 'revisions' | 'approvals' | 'stores', { label: string; value: number | null }>;
type ActivityPayload = { events: ActivityEvent[] };

type HubStore = { id: string; name: string; status?: string };
type HubDrop = {
  id: string;
  name: string;
  scheduledFor?: string | null;
  scheduledForLocal?: string | null;
  templateId?: string | null;
};
type HubPayload = {
  live?: { count: number; withErrors: number };
  artwork: { count: number; stores: HubStore[] };
  drops: { upcomingCount: number; upcoming: HubDrop[] };
  payouts: {
    lastQuarter: string;
    lastQuarterLabel: string;
    // null on all three when the teamStorePayouts read FAILED. That is not the
    // same as zero statements, and the tiles must not render it as one.
    count: number | null;
    teamTotal: number | null;
    due: boolean | null;
    unavailable?: boolean;
  };
};

const HUB_LINKS = [
  { label: 'Active Stores', to: '/active-stores', icon: Store },
  { label: 'Create Store', to: '/create-store', icon: CirclePlus },
  { label: 'Payouts', to: '/payouts', icon: Banknote },
  { label: 'Chat', to: '/chat', icon: MessageSquare },
  { label: 'Reports', to: '/reports', icon: BarChart3 },
] as const;

// Only show a name in the greeting if the email looks like firstname.lastname@.
// Shared inboxes like orders@/info@/admin@ get a clean "Good afternoon."
function pickGreetingName(email?: string): string {
  if (!email) return '';
  const username = email.split('@')[0] || '';
  const parts = username.split('.');
  if (parts.length < 2) return '';
  const first = parts[0];
  if (!first || first.length < 2) return '';
  return first.charAt(0).toUpperCase() + first.slice(1);
}

export default function Today() {
  const { user } = useSession();
  const { data: kpis, isLoading: kpisLoading } = useSWR<KpiPayload>('/api/today/kpis', get, { refreshInterval: 60_000 });
  const { data: hub, isLoading: hubLoading } = useSWR<HubPayload>('/api/today/hub', get, { refreshInterval: 60_000 });
  const { data: activity, isLoading: activityLoading } = useSWR<ActivityPayload>('/api/today/activity?limit=20', get, { refreshInterval: 60_000 });

  const niceFirst = pickGreetingName(user?.email);
  const liveCount = hub?.live?.count ?? kpis?.stores.value;
  const liveErrors = hub?.live?.withErrors ?? 0;
  const artwork = hub?.artwork;
  const payouts = hub?.payouts;
  const prizeNeeds = (kpis?.pending.value ?? 0) + (kpis?.revisions.value ?? 0);

  return (
    <div className="px-7 py-6">
      <div className="text-[22px] font-semibold tracking-tighter">
        {greeting()}{niceFirst ? `, ${niceFirst}` : ''}
      </div>
      <div className="mb-5 text-[12px] text-muted">
        {todayLine()}
        {typeof liveCount === 'number' && ` · ${liveCount} live store${liveCount === 1 ? '' : 's'}`}
        {liveErrors > 0 && ` · ${liveErrors} with errors`}
      </div>

      <section className="mb-6">
        <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Store hub</h2>
        <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Link to="/active-stores" className="block">
            <KpiTile label="Stores Live" value={liveCount ?? undefined} loading={hubLoading && kpisLoading} />
          </Link>
          <Link to="/team-store-submissions" className="block">
            <KpiTile label="Needs artwork" value={artwork?.count ?? undefined} loading={hubLoading} />
          </Link>
          <Link to="/payouts" className="block">
            <KpiTile
              label={payouts ? `Payouts ${payouts.lastQuarterLabel}` : 'Last quarter'}
              value={payouts?.unavailable ? 'Unavailable' : (payouts?.count ?? undefined)}
              loading={hubLoading}
            />
          </Link>
          <Link to="/payouts" className="block">
            <KpiTile
              label="Payouts due"
              value={
                hubLoading ? undefined
                  : payouts?.unavailable ? 'Unavailable'
                  : payouts?.due ? 'None yet'
                  : dollars(payouts?.teamTotal)
              }
              loading={hubLoading}
            />
          </Link>
        </div>

        <div className="mb-3 flex flex-wrap gap-2">
          {HUB_LINKS.map((item) => {
            const Icon = item.icon;
            return (
              <Button key={item.to} asChild size="sm" variant="secondary">
                <Link to={item.to}>
                  <Icon className="h-3.5 w-3.5" aria-hidden /> {item.label}
                </Link>
              </Button>
            );
          })}
        </div>

        <div className="grid gap-3 lg:grid-cols-2">
          <HubList
            title="Needs artwork"
            href="/team-store-submissions"
            loading={hubLoading}
            empty="No stores waiting on artwork."
          >
            {(artwork?.stores || []).map((s) => (
              <Link
                key={s.id}
                to={storePath(s.id)}
                className="flex items-center justify-between gap-2 border-b border-divider py-1.5 last:border-b-0 hover:bg-bg/50"
              >
                <span className="truncate text-[12px]">{s.name}</span>
                <span className="shrink-0 text-[11px] text-muted">pending artwork</span>
              </Link>
            ))}
          </HubList>

          {/* No "Scheduled drops" panel: storeDrops is written by the fleet
              rollout service (#355), which is not deployed. An always-empty
              list reads as "nothing scheduled" rather than "no scheduler". */}
        </div>

        <div className="mt-3 rounded-md bg-surface p-3 shadow-card">
          <div className="mb-1 flex items-center justify-between px-1">
            <div className="text-[12px] font-semibold">
              Last quarter{payouts ? ` · ${payouts.lastQuarterLabel}` : ''}
            </div>
            <Link to="/payouts" className="text-[11.5px] text-primary underline-offset-2 hover:underline">
              Payouts →
            </Link>
          </div>
          {hubLoading ? (
            <Skeleton className="mx-1 my-2 h-4 w-2/3" />
          ) : payouts?.unavailable ? (
            <p className="px-1 py-3 text-center text-[11px] text-danger">
              Could not read teamStorePayouts. This is not "nobody was paid" — retry before acting on it.
            </p>
          ) : payouts && !payouts.due ? (
            <p className="px-1 py-1 text-[12px] text-text">
              {payouts.count} statement{payouts.count === 1 ? '' : 's'} · team {dollars(payouts.teamTotal)}
            </p>
          ) : (
            <p className="px-1 py-3 text-center text-[11px] text-muted">
              No last-quarter statements yet. Stores stay open; payouts run each quarter.
            </p>
          )}
        </div>
      </section>

      <section className="mb-5">
        <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Prize queue</h2>
        <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          <Link to="/submissions" className="block">
            <KpiTile label="Pending" value={kpis?.pending.value ?? undefined} loading={kpisLoading} />
          </Link>
          <Link to="/revisions" className="block">
            <KpiTile label="Revisions" value={kpis?.revisions.value ?? undefined} loading={kpisLoading} />
          </Link>
          <Link to="/pending-approvals" className="block">
            <KpiTile label="Approvals" value={kpis?.approvals.value ?? undefined} loading={kpisLoading} />
          </Link>
        </div>
        {prizeNeeds > 0 && (
          <p className="mb-3 text-[11.5px] text-muted">
            {prizeNeeds} prize submission{prizeNeeds === 1 ? '' : 's'} waiting — not the store list.
          </p>
        )}
      </section>

      <div className="rounded-md bg-surface p-3 shadow-card">
        <div className="mb-1 px-1 text-[12px] font-semibold">Recent Activity</div>
        {activityLoading ? (
          <div className="space-y-2 px-1 py-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : activity && activity.events.length > 0 ? (
          <div className="px-1">
            {activity.events.map((e) => <ActivityFeedItem key={e.id} event={e} />)}
          </div>
        ) : (
          <div className="px-1 py-3 text-center text-[11px] text-muted">No recent activity.</div>
        )}
      </div>
    </div>
  );
}

function HubList({
  title,
  href,
  loading,
  empty,
  children,
}: {
  title: string;
  href: string;
  loading: boolean;
  empty: string;
  children: ReactNode;
}) {
  const items = Array.isArray(children) ? children : [children];
  const hasRows = items.filter(Boolean).length > 0;
  return (
    <div className="rounded-md bg-surface p-3 shadow-card">
      <div className="mb-1 flex items-center justify-between px-1">
        <div className="text-[12px] font-semibold">{title}</div>
        <Link to={href} className="text-[11.5px] text-primary underline-offset-2 hover:underline">Open →</Link>
      </div>
      {loading ? (
        <div className="space-y-2 px-1 py-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      ) : hasRows ? (
        <div className="px-1">{children}</div>
      ) : (
        <div className="px-1 py-3 text-center text-[11px] text-muted">{empty}</div>
      )}
    </div>
  );
}
